/**
 * @ipc/admin-ui · entities/analytics · model/mappers — unknown → DTO БЕЗ as-кастов
 * (type-guard-нарраверы @/shared/api). Каждый toXxx возвращает null на неопознанной форме;
 * списки собираются через mapList и отфильтровывают null (см. brief: паттерн @acc api-client).
 */
import { isPlainObject, str, num, optStr, optNum, mapList } from '@/shared/api';
import type {
  StatusBucket,
  AnalyticsRefunds,
  AnalyticsTimeToPay,
  SummaryResponse,
  BreakdownRow,
  BreakdownResponse,
  TimeseriesPoint,
  TimeseriesResponse,
  AnalyticsPaymentRow,
  PaymentsTableResponse,
} from './types';

function toStatusBucket(raw: unknown): StatusBucket | null {
  if (!isPlainObject(raw)) return null;
  return { status: str(raw['status']), count: num(raw['count']), amountTiyin: num(raw['amountTiyin']) };
}

function toRefunds(raw: unknown): AnalyticsRefunds {
  const obj = isPlainObject(raw) ? raw : {};
  return {
    refundedPaymentCount: num(obj['refundedPaymentCount']),
    successfulRefundAmountTiyin: num(obj['successfulRefundAmountTiyin']),
    refundRate: num(obj['refundRate']),
    fullRefundCount: num(obj['fullRefundCount']),
    partialRefundCount: num(obj['partialRefundCount']),
  };
}

function toTimeToPay(raw: unknown): AnalyticsTimeToPay {
  const obj = isPlainObject(raw) ? raw : {};
  return {
    sampleSize: num(obj['sampleSize']),
    avgSeconds: num(obj['avgSeconds']),
    p50Seconds: num(obj['p50Seconds']),
    p90Seconds: num(obj['p90Seconds']),
  };
}

export function toSummaryResponse(raw: unknown): SummaryResponse | null {
  if (!isPlainObject(raw)) return null;
  const period = isPlainObject(raw['period']) ? raw['period'] : {};
  return {
    period: { from: str(period['from']), to: str(period['to']) },
    createdCount: num(raw['createdCount']),
    totalAmountTiyin: num(raw['totalAmountTiyin']),
    confirmedCount: num(raw['confirmedCount']),
    confirmedAmountTiyin: num(raw['confirmedAmountTiyin']),
    conversionRate: num(raw['conversionRate']),
    averageTicketTiyin: num(raw['averageTicketTiyin']),
    medianTicketTiyin: num(raw['medianTicketTiyin']),
    byStatus: mapList(raw['byStatus'], toStatusBucket),
    refunds: toRefunds(raw['refunds']),
    timeToPay: toTimeToPay(raw['timeToPay']),
  };
}

function toBreakdownRow(raw: unknown): BreakdownRow | null {
  if (!isPlainObject(raw)) return null;
  return {
    key: str(raw['key']),
    count: num(raw['count']),
    amountTiyin: num(raw['amountTiyin']),
    confirmedCount: num(raw['confirmedCount']),
    share: num(raw['share']),
  };
}

export function toBreakdownResponse(raw: unknown): BreakdownResponse | null {
  if (!isPlainObject(raw)) return null;
  return {
    groupBy: str(raw['groupBy']),
    totalCount: num(raw['totalCount']),
    rows: mapList(raw['rows'], toBreakdownRow),
  };
}

function toTimeseriesPoint(raw: unknown): TimeseriesPoint | null {
  if (!isPlainObject(raw)) return null;
  return {
    bucket: str(raw['bucket']),
    createdCount: num(raw['createdCount']),
    confirmedCount: num(raw['confirmedCount']),
    confirmedAmountTiyin: num(raw['confirmedAmountTiyin']),
    conversionRate: num(raw['conversionRate']),
  };
}

export function toTimeseriesResponse(raw: unknown): TimeseriesResponse | null {
  if (!isPlainObject(raw)) return null;
  const granularityRaw = str(raw['granularity'], 'day');
  const granularity = granularityRaw === 'week' || granularityRaw === 'month' ? granularityRaw : 'day';
  return { granularity, points: mapList(raw['points'], toTimeseriesPoint) };
}

export function toAnalyticsPaymentRow(raw: unknown): AnalyticsPaymentRow | null {
  if (!isPlainObject(raw)) return null;
  return {
    id: num(raw['id']),
    intentId: num(raw['intentId']),
    merchantId: num(raw['merchantId']),
    invoiceId: str(raw['invoiceId']),
    providerUuid: str(raw['providerUuid']),
    provider: str(raw['provider']),
    ps: optStr(raw['ps']),
    status: str(raw['status']),
    totalAmountTiyin: optNum(raw['totalAmountTiyin']),
    refundAmountTiyin: num(raw['refundAmountTiyin']),
    currency: str(raw['currency']),
    createdAt: str(raw['createdAt']),
    paidAt: optStr(raw['paidAt']),
  };
}

export function toPaymentsTableResponse(raw: unknown): PaymentsTableResponse | null {
  if (!isPlainObject(raw)) return null;
  return {
    items: mapList(raw['items'], toAnalyticsPaymentRow),
    nextCursor: optNum(raw['nextCursor']),
  };
}
